import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Package, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Plus,
  Trash2,
  Search
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StorePendingIssuesPage = () => {
  const [demands, setDemands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [workOrderFilter, setWorkOrderFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null); // Single expanded row (accordion style)
  const [issueQuantities, setIssueQuantities] = useState({});
  const [confirmingDemandId, setConfirmingDemandId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, demand: null });
  const [stats, setStats] = useState({ pending: 0, partial: 0, completed: 0 });

  // Manual Demand Creation State
  const [manualDemandDialog, setManualDemandDialog] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [manualDemandForm, setManualDemandForm] = useState({
    items: [],
    purpose: '',
    remarks: ''
  });
  const [creatingDemand, setCreatingDemand] = useState(false);

  const fetchDemands = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (workOrderFilter !== 'all') {
        params.work_order_id = workOrderFilter;
      }
      
      const response = await axios.get(`${API_URL}/api/store/pending-bin-card-demands`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setDemands(response.data);
      
      // Calculate stats
      const pending = response.data.filter(d => d.status === 'pending').length;
      const partial = response.data.filter(d => d.status === 'partially_issued').length;
      const completed = response.data.filter(d => d.status === 'completed').length;
      setStats({ pending, partial, completed });
      
    } catch (error) {
      console.error('Error fetching demands:', error);
      toast.error('Failed to load pending issues');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, workOrderFilter]);

  const fetchAllItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to items with bin_card_stock > 0
      const itemsWithStock = response.data.filter(item => (item.bin_card_stock || 0) > 0);
      setAllItems(itemsWithStock);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    fetchDemands();
  }, [fetchDemands]);

  const toggleRow = (demandId) => {
    const willExpand = expandedRow !== demandId;
    setExpandedRow(willExpand ? demandId : null);
    
    // Initialize issue/receive quantities for this demand if not set
    const demand = demands.find(d => d.id === demandId);
    if (willExpand && demand && !issueQuantities[demandId]) {
      const quantities = {};
      demand.items.forEach(item => {
        // For BPD receive, use quantity_returned; for regular issue, use quantity_demanded
        const pending = demand.demand_type === 'bpd_receive'
          ? (item.quantity_returned || 0) - (item.quantity_received || 0)
          : (item.quantity_demanded || 0) - (item.quantity_issued || 0);
        quantities[item.item_id] = pending;
      });
      setIssueQuantities(prev => ({
        ...prev,
        [demandId]: quantities
      }));
    }
  };

  const handleQuantityChange = (demandId, itemId, value) => {
    const numValue = parseFloat(value) || 0;
    setIssueQuantities(prev => ({
      ...prev,
      [demandId]: {
        ...(prev[demandId] || {}),
        [itemId]: numValue
      }
    }));
  };

  const openConfirmDialog = (demand) => {
    setConfirmDialog({ open: true, demand });
  };

  const handleConfirmIssue = async () => {
    const demand = confirmDialog.demand;
    if (!demand) return;
    
    const isBpdReceive = demand.demand_type === 'bpd_receive';
    const isPurchaseReturn = demand.demand_type === 'purchase_return';
    
    try {
      setConfirmingDemandId(demand.id);
      const token = localStorage.getItem('token');
      
      // For BPD receive, use quantity_returned; for regular issue, use quantity_demanded
      const items = demand.items
        .filter(item => {
          const qty = issueQuantities[demand.id]?.[item.item_id] || 0;
          return qty > 0;
        })
        .map(item => ({
          item_id: item.item_id,
          [isBpdReceive ? 'qty_received' : isPurchaseReturn ? 'qty_released' : 'qty_issued']: issueQuantities[demand.id]?.[item.item_id] || 0
        }));
      
      if (items.length === 0) {
        toast.error(isBpdReceive ? 'No items to receive' : isPurchaseReturn ? 'No items to release' : 'No items to issue');
        return;
      }
      
      // Use different endpoint for BPD receive and purchase return
      const endpoint = isBpdReceive 
        ? `${API_URL}/api/store/pending-bin-card-demands/${demand.id}/confirm-receive`
        : isPurchaseReturn
        ? `${API_URL}/api/store/pending-bin-card-demands/${demand.id}/confirm-release`
        : `${API_URL}/api/store/pending-bin-card-demands/${demand.id}/confirm`;
      
      const response = await axios.post(
        endpoint,
        { items, remarks: `Confirmed from Store Dashboard (${isBpdReceive ? 'BPD Receive' : isPurchaseReturn ? 'Purchase Return Release' : 'Issue'})` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const successMsg = isBpdReceive 
        ? `Receive confirmed: ${response.data.receive_no}`
        : isPurchaseReturn
        ? `Release confirmed. DC: ${response.data.dc_number}, GP: ${response.data.gp_number} created.`
        : `Issue confirmed: ${response.data.issue_no}`;
      toast.success(successMsg);
      setConfirmDialog({ open: false, demand: null });
      fetchDemands();
      
    } catch (error) {
      console.error('Error confirming:', error);
      const errorMsg = error.response?.data?.detail || `Failed to confirm ${isBpdReceive ? 'receive' : 'issue'}`;
      if (typeof errorMsg === 'object' && errorMsg.errors) {
        errorMsg.errors.forEach(e => toast.error(e));
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setConfirmingDemandId(null);
    }
  };

  // Manual Demand Functions
  const openManualDemandDialog = () => {
    fetchAllItems();
    setManualDemandForm({ items: [], purpose: '', remarks: '' });
    setItemSearchQuery('');
    setManualDemandDialog(true);
  };

  const addItemToManualDemand = (item) => {
    // Check if already added
    if (manualDemandForm.items.find(i => i.item_id === item.id)) {
      toast.error('Item already added');
      return;
    }
    
    setManualDemandForm(prev => ({
      ...prev,
      items: [...prev.items, {
        item_id: item.id,
        item_name: item.name || item.item_name || item.item_code,
        item_code: item.item_code,
        quantity: '',
        unit: item.unit || 'NOS',
        bin_card_stock: item.bin_card_stock || 0
      }]
    }));
    setItemSearchQuery('');
  };

  const updateManualDemandItemQty = (itemId, qty) => {
    setManualDemandForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.item_id === itemId ? { ...item, quantity: qty } : item
      )
    }));
  };

  const removeItemFromManualDemand = (itemId) => {
    setManualDemandForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.item_id !== itemId)
    }));
  };

  const handleCreateManualDemand = async () => {
    if (manualDemandForm.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    if (!manualDemandForm.purpose.trim()) {
      toast.error('Please enter a purpose');
      return;
    }
    
    // Validate quantities
    for (const item of manualDemandForm.items) {
      const qty = parseFloat(item.quantity);
      if (!item.quantity || isNaN(qty) || qty <= 0) {
        toast.error(`Quantity is required for ${item.item_name}`);
        return;
      }
      if (qty > item.bin_card_stock) {
        toast.error(`Insufficient stock for ${item.item_name}. Available: ${item.bin_card_stock}`);
        return;
      }
    }
    
    try {
      setCreatingDemand(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_URL}/api/store/pending-bin-card-demands/manual`,
        {
          items: manualDemandForm.items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            quantity: parseFloat(item.quantity),
            unit: item.unit
          })),
          purpose: manualDemandForm.purpose.toUpperCase(),
          remarks: manualDemandForm.remarks || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Manual demand ${response.data.demand_number} created`);
      setManualDemandDialog(false);
      fetchDemands();
      
    } catch (error) {
      console.error('Error creating manual demand:', error);
      toast.error(error.response?.data?.detail || 'Failed to create demand');
    } finally {
      setCreatingDemand(false);
    }
  };

  const filteredSearchItems = allItems.filter(item => {
    if (!itemSearchQuery.trim()) return false;
    const query = itemSearchQuery.toLowerCase();
    return (
      (item.item_code || '').toLowerCase().includes(query) ||
      (item.name || '').toLowerCase().includes(query) ||
      (item.item_name || '').toLowerCase().includes(query)
    );
  }).slice(0, 10); // Limit to 10 results

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">Pending</Badge>;
      case 'partially_issued':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">Partial</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDemandTypeBadge = (demandType, bpdType) => {
    if (demandType === 'manual') {
      return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300 text-xs ml-2">Manual</Badge>;
    }
    if (demandType === 'bpd_receive') {
      const label = bpdType === 'material_return' ? 'Material Return' : 'By-product';
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs ml-2">{label}</Badge>;
    }
    if (demandType === 'purchase_return') {
      return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-xs ml-2">Purchase Return</Badge>;
    }
    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  // Get unique work orders for filter
  const workOrders = [...new Set(demands.map(d => d.work_order_id).filter(Boolean))];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="store-pending-issues-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Store Dashboard</h1>
            <p className="text-gray-500">Pending Material Issues</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={openManualDemandDialog} variant="default" data-testid="create-manual-demand-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Manual Demand
            </Button>
            <Button onClick={fetchDemands} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="pending-card">
            <CardContent className="p-4 flex items-center space-x-4">
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="partial-card">
            <CardContent className="p-4 flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Partially Issued</p>
                <p className="text-2xl font-bold text-blue-600">{stats.partial}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="completed-card">
            <CardContent className="p-4 flex items-center space-x-4">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="w-48">
                <label className="text-sm text-gray-500 mb-1 block">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter} data-testid="status-filter">
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partially_issued">Partially Issued</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-48">
                <label className="text-sm text-gray-500 mb-1 block">Work Order</label>
                <Select value={workOrderFilter} onValueChange={setWorkOrderFilter} data-testid="wo-filter">
                  <SelectTrigger>
                    <SelectValue placeholder="All Work Orders" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Work Orders</SelectItem>
                    {workOrders.map(woId => {
                      const demand = demands.find(d => d.work_order_id === woId);
                      return (
                        <SelectItem key={woId} value={woId}>
                          {demand?.work_order_number || woId}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-48">
                <label className="text-sm text-gray-500 mb-1 block">Type</label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="jc_issue">JC Issue</SelectItem>
                    <SelectItem value="bpd_receive">BPD Receive</SelectItem>
                    <SelectItem value="purchase_return">Purchase Return</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Demands Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Pending Demands
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : demands.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No pending demands found
              </div>
            ) : (
              <Table data-testid="demands-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Job/Demand No.</TableHead>
                    <TableHead>Work Order</TableHead>
                    <TableHead>Purpose/Process</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {demands.filter(d => typeFilter === 'all' || d.demand_type === typeFilter || (typeFilter === 'jc_issue' && !['bpd_receive','purchase_return','manual'].includes(d.demand_type))).map(demand => (
                    <React.Fragment key={demand.id}>
                      <TableRow 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleRow(demand.id)}
                        data-testid={`demand-row-${demand.id}`}
                      >
                        <TableCell>
                          {expandedRow === demand.id ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {demand.demand_type === 'bpd_receive' ? 'BPD Receive' : 
                           demand.demand_type === 'purchase_return' ? (demand.demand_number || 'Purchase Return') : 
                           demand.job_number}
                          {getDemandTypeBadge(demand.demand_type, demand.bpd_type)}
                        </TableCell>
                        <TableCell>
                          {demand.demand_type === 'purchase_return' 
                            ? (demand.supplier_name || '-') 
                            : (demand.work_order_number || '-')}
                        </TableCell>
                        <TableCell>
                          {demand.demand_type === 'purchase_return' 
                            ? (demand.po_number || '-') 
                            : (demand.process_name || demand.purpose || '-')}
                        </TableCell>
                        <TableCell>{demand.items?.length || 0} items</TableCell>
                        <TableCell>{formatDate(demand.created_at)}</TableCell>
                        <TableCell>{getStatusBadge(demand.status)}</TableCell>
                        <TableCell className="text-right">
                          {demand.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="default"
                              className={
                                demand.demand_type === 'bpd_receive' ? 'bg-green-600 hover:bg-green-700' : 
                                demand.demand_type === 'purchase_return' ? 'bg-orange-600 hover:bg-orange-700' : ''
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (expandedRow !== demand.id) {
                                  toggleRow(demand.id);
                                }
                                openConfirmDialog(demand);
                              }}
                              data-testid={`issue-btn-${demand.id}`}
                            >
                              {demand.demand_type === 'bpd_receive' ? 'Receive' : 
                               demand.demand_type === 'purchase_return' ? 'Release' : 'Issue'}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Row */}
                      {expandedRow === demand.id && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-gray-50 p-0">
                            <div className="p-4">
                              {/* Different table for Issue vs Receive demands */}
                              {demand.demand_type === 'bpd_receive' ? (
                                /* BPD Receive Table */
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Item</TableHead>
                                      <TableHead>Location</TableHead>
                                      <TableHead className="text-right">Returned</TableHead>
                                      <TableHead className="text-right">Received</TableHead>
                                      <TableHead className="text-right">Pending</TableHead>
                                      <TableHead className="text-right">Bin Stock</TableHead>
                                      <TableHead className="text-right w-32">Qty to Receive</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {demand.items?.map(item => {
                                      const pending = (item.quantity_returned || 0) - (item.quantity_received || 0);
                                      const binStock = item.current_bin_card_stock || 0;
                                      const qtyToReceive = issueQuantities[demand.id]?.[item.item_id] ?? pending;
                                      
                                      return (
                                        <TableRow key={item.item_id}>
                                          <TableCell className="font-medium">{item.item_name}</TableCell>
                                          <TableCell>
                                            {item.sub_location ? (
                                              <span className="text-xs font-mono bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                                {item.sub_location}
                                              </span>
                                            ) : (
                                              <span className="text-xs text-gray-400">-</span>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-right">{item.quantity_returned || 0}</TableCell>
                                          <TableCell className="text-right">{item.quantity_received || 0}</TableCell>
                                          <TableCell className="text-right font-medium text-green-600">{pending}</TableCell>
                                          <TableCell className="text-right">{binStock}</TableCell>
                                          <TableCell className="text-right">
                                            {demand.status !== 'completed' && pending > 0 && (
                                              <Input
                                                type="number"
                                                min="0"
                                                max={pending}
                                                value={qtyToReceive}
                                                onChange={(e) => handleQuantityChange(demand.id, item.item_id, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-24 text-right"
                                                data-testid={`qty-input-${demand.id}-${item.item_id}`}
                                              />
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              ) : demand.demand_type === 'purchase_return' ? (
                                /* Purchase Return Table */
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Item</TableHead>
                                      <TableHead>Reason</TableHead>
                                      <TableHead className="text-right">To Return</TableHead>
                                      <TableHead className="text-right">Released</TableHead>
                                      <TableHead className="text-right">Pending</TableHead>
                                      <TableHead className="text-right">Bin Stock</TableHead>
                                      <TableHead className="text-right w-32">Qty to Release</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {demand.items?.map(item => {
                                      const pending = (item.quantity_demanded || 0) - (item.quantity_issued || 0);
                                      const binStock = item.current_bin_card_stock || 0;
                                      const qtyToRelease = issueQuantities[demand.id]?.[item.item_id] ?? pending;
                                      
                                      return (
                                        <TableRow key={item.item_id}>
                                          <TableCell className="font-medium">{item.item_name}</TableCell>
                                          <TableCell className="text-sm text-gray-600">{item.reason || '-'}</TableCell>
                                          <TableCell className="text-right">{item.quantity_demanded || 0}</TableCell>
                                          <TableCell className="text-right">{item.quantity_issued || 0}</TableCell>
                                          <TableCell className="text-right font-medium text-orange-600">{pending}</TableCell>
                                          <TableCell className="text-right">
                                            <span className={binStock < pending ? 'text-red-600 font-medium' : ''}>
                                              {binStock}
                                            </span>
                                          </TableCell>
                                          <TableCell className="text-right">
                                            {demand.status !== 'completed' && pending > 0 && (
                                              <Input
                                                type="number"
                                                min="0"
                                                max={Math.min(pending, binStock)}
                                                value={qtyToRelease}
                                                onChange={(e) => handleQuantityChange(demand.id, item.item_id, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-24 text-right"
                                              />
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              ) : (
                                /* Standard Issue Table */
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Item</TableHead>
                                      <TableHead>Location</TableHead>
                                      <TableHead className="text-right">Demanded</TableHead>
                                      <TableHead className="text-right">Issued</TableHead>
                                      <TableHead className="text-right">Pending</TableHead>
                                      <TableHead className="text-right">Bin Stock</TableHead>
                                      <TableHead className="text-right w-32">Qty to Issue</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {demand.items?.map(item => {
                                      const pending = (item.quantity_demanded || 0) - (item.quantity_issued || 0);
                                      const binStock = item.current_bin_card_stock || 0;
                                      const qtyToIssue = issueQuantities[demand.id]?.[item.item_id] ?? pending;
                                      
                                      return (
                                        <TableRow key={item.item_id}>
                                          <TableCell className="font-medium">
                                            {item.item_name}
                                            {item.is_additional && (
                                              <Badge variant="outline" className="ml-2 text-xs">Additional</Badge>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {item.sub_location ? (
                                              <span className="text-xs font-mono bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                                {item.sub_location}
                                              </span>
                                            ) : (
                                              <span className="text-xs text-gray-400">-</span>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-right">{item.quantity_demanded}</TableCell>
                                          <TableCell className="text-right">{item.quantity_issued || 0}</TableCell>
                                          <TableCell className="text-right font-medium text-orange-600">{pending}</TableCell>
                                          <TableCell className="text-right">
                                            <span className={binStock < pending ? 'text-red-600 font-medium' : ''}>
                                              {binStock}
                                            </span>
                                          </TableCell>
                                          <TableCell className="text-right">
                                            {demand.status !== 'completed' && pending > 0 && (
                                              <Input
                                                type="number"
                                                min="0"
                                                max={Math.min(pending, binStock)}
                                                value={qtyToIssue}
                                                onChange={(e) => handleQuantityChange(demand.id, item.item_id, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-24 text-right"
                                                data-testid={`qty-input-${demand.id}-${item.item_id}`}
                                              />
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                              
                              {demand.status !== 'completed' && (
                                <div className="mt-4 flex justify-end">
                                  <Button
                                    className={
                                      demand.demand_type === 'bpd_receive' ? 'bg-green-600 hover:bg-green-700' : 
                                      demand.demand_type === 'purchase_return' ? 'bg-orange-600 hover:bg-orange-700' : ''
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openConfirmDialog(demand);
                                    }}
                                    data-testid={`confirm-btn-${demand.id}`}
                                  >
                                    {demand.demand_type === 'bpd_receive' ? 'Confirm Receive' : 
                                     demand.demand_type === 'purchase_return' ? 'Confirm Release' : 'Confirm Issue'}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, demand: confirmDialog.demand })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmDialog.demand?.demand_type === 'bpd_receive' ? 'Confirm Material Receive' : 'Confirm Material Issue'}
              </DialogTitle>
            </DialogHeader>
            
            {confirmDialog.demand && (
              <div className="space-y-4">
                <div className="text-sm text-gray-500">
                  <p>
                    {confirmDialog.demand.demand_type === 'bpd_receive' ? 'BPD Receive' : 
                     confirmDialog.demand.demand_type === 'purchase_return' ? 'Purchase Return' : 'Job/Demand'}: 
                    <span className="font-medium text-gray-900 ml-1">
                      {confirmDialog.demand.demand_type === 'bpd_receive' 
                        ? (confirmDialog.demand.bpd_type === 'material_return' ? 'Material Return' : 'By-product')
                        : confirmDialog.demand.demand_type === 'purchase_return'
                        ? `${confirmDialog.demand.supplier_name} (${confirmDialog.demand.po_number})`
                        : confirmDialog.demand.job_number}
                    </span>
                  </p>
                  {confirmDialog.demand.work_order_number && confirmDialog.demand.demand_type !== 'purchase_return' && (
                    <p>Work Order: <span className="font-medium text-gray-900">{confirmDialog.demand.work_order_number}</span></p>
                  )}
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">
                          {confirmDialog.demand.demand_type === 'bpd_receive' ? 'Qty to Receive' : 
                           confirmDialog.demand.demand_type === 'purchase_return' ? 'Qty to Release' : 'Qty to Issue'}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {confirmDialog.demand.items?.map(item => {
                        const qty = issueQuantities[confirmDialog.demand.id]?.[item.item_id] || 0;
                        if (qty <= 0) return null;
                        return (
                          <TableRow key={item.item_id}>
                            <TableCell>{item.item_name}</TableCell>
                            <TableCell className="text-right font-medium">{qty}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                
                <p className="text-sm text-gray-500">
                  {confirmDialog.demand.demand_type === 'bpd_receive' 
                    ? 'This will increment the bin card stock for the above items.'
                    : confirmDialog.demand.demand_type === 'purchase_return'
                    ? 'This will create a Delivery Challan, Gate Pass, and Debit Note for supplier return.'
                    : 'This will decrement the bin card stock for the above items.'}
                </p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, demand: null })}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmIssue}
                disabled={confirmingDemandId === confirmDialog.demand?.id}
                className={confirmDialog.demand?.demand_type === 'bpd_receive' ? 'bg-green-600 hover:bg-green-700' : ''}
                data-testid="confirm-dialog-btn"
              >
                {confirmingDemandId === confirmDialog.demand?.id ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Confirming...
                  </>
                ) : (
                  confirmDialog.demand?.demand_type === 'bpd_receive' ? 'Confirm Receive' : 'Confirm Issue'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual Demand Creation Dialog */}
        <Dialog open={manualDemandDialog} onOpenChange={setManualDemandDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Manual Demand</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Purpose */}
              <div className="space-y-2">
                <Label>Purpose *</Label>
                <Select 
                  value={manualDemandForm.purpose} 
                  onValueChange={(val) => setManualDemandForm(prev => ({ ...prev, purpose: val }))}
                >
                  <SelectTrigger data-testid="manual-demand-purpose">
                    <SelectValue placeholder="Select purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRODUCTION">Production</SelectItem>
                    <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                    <SelectItem value="SAMPLE">Sample</SelectItem>
                    <SelectItem value="REWORK">Rework</SelectItem>
                    <SelectItem value="TRANSFER">Transfer</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Item Search */}
              <div className="space-y-2">
                <Label>Add Items</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search by item code or name..."
                    value={itemSearchQuery}
                    onChange={(e) => setItemSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="manual-demand-item-search"
                  />
                </div>
                
                {/* Search Results */}
                {filteredSearchItems.length > 0 && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {filteredSearchItems.map(item => (
                      <div 
                        key={item.id}
                        className="p-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center border-b last:border-0"
                        onClick={() => addItemToManualDemand(item)}
                      >
                        <div>
                          <p className="font-medium text-sm">{item.item_code}</p>
                          <p className="text-xs text-gray-500">{item.name || item.item_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-green-600">Stock: {(item.bin_card_stock || 0).toFixed(2)}</p>
                          <Button size="sm" variant="ghost" className="h-6 px-2">
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Items */}
              {manualDemandForm.items.length > 0 && (
                <div className="space-y-2">
                  <Label>Selected Items ({manualDemandForm.items.length})</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Stock</TableHead>
                          <TableHead className="w-24">Qty</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {manualDemandForm.items.map(item => (
                          <TableRow key={item.item_id}>
                            <TableCell>
                              <p className="font-medium text-sm">{item.item_code}</p>
                              <p className="text-xs text-gray-500">{item.item_name}</p>
                            </TableCell>
                            <TableCell className="text-right text-sm text-green-600">
                              {item.bin_card_stock}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                max={item.bin_card_stock}
                                value={item.quantity}
                                onChange={(e) => updateManualDemandItemQty(item.item_id, e.target.value)}
                                className="w-20 text-right"
                              />
                            </TableCell>
                            <TableCell>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-8 w-8 p-0 text-red-500"
                                onClick={() => removeItemFromManualDemand(item.item_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Remarks */}
              <div className="space-y-2">
                <Label>Remarks (Optional)</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={manualDemandForm.remarks}
                  onChange={(e) => setManualDemandForm(prev => ({ ...prev, remarks: e.target.value }))}
                  rows={2}
                  data-testid="manual-demand-remarks"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setManualDemandDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateManualDemand}
                disabled={creatingDemand || manualDemandForm.items.length === 0}
                data-testid="create-manual-demand-submit"
              >
                {creatingDemand ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Demand'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default StorePendingIssuesPage;
